import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Suspense, lazy, useEffect, useMemo } from 'react'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { ApiError, onUnauthenticated } from './api/client'
import { useMe, useSettings } from './api/hooks'
import { RequireAuth } from './auth/RequireAuth'
import { AdminLoginPage } from './auth/AdminLoginPage'
import { LoginPage } from './auth/LoginPage'
import { AppShell } from './layout/AppShell'
import { ErrorBoundary } from './layout/ErrorBoundary'
import { FullPageSpinner } from './layout/FullPageSpinner'
import { LinkWarning } from './pages/LinkWarning'
import { MailPage } from './mail/MailPage'
import { ScheduledPage } from './mail/ScheduledPage'
import { buildTheme } from './theme'

const SetupPage = lazy(() => import('./pages/SetupPage'))
const ForcedSetupPage = lazy(() => import('./auth/ForcedSetupPage'))
const ForgotPasswordPage = lazy(() => import('./auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./auth/ResetPasswordPage'))
const SettingsPage = lazy(() => import('./settings/SettingsPage'))
const AdminPage = lazy(() => import('./admin/AdminPage'))
const AdminShell = lazy(() => import('./admin/AdminShell').then((m) => ({ default: m.AdminShell })))
const CalendarPage = lazy(() => import('./calendar/CalendarPage'))
const ContactsPage = lazy(() => import('./contacts/ContactsPage'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
})

function Themed({ children }: { children: React.ReactNode }) {
  const settings = useSettings()
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')
  // Until the account's setting loads, use the last mode this browser showed so dark users never get a white flash.
  const cached = (() => { try { return localStorage.getItem('orbimail-theme') as 'light' | 'dark' | 'system' | null } catch { return null } })()
  const pref = settings.data?.theme ?? cached ?? 'system'
  useEffect(() => { if (settings.data?.theme) { try { localStorage.setItem('orbimail-theme', settings.data.theme) } catch { /* private mode */ } } }, [settings.data?.theme])
  const mode = pref === 'system' ? (prefersDark ? 'dark' : 'light') : pref
  const theme = useMemo(() => buildTheme(mode, settings.data?.density ?? 'compact'), [mode, settings.data?.density])
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  )
}

function Routing() {
  const navigate = useNavigate()
  useEffect(() => onUnauthenticated(() => { queryClient.clear(); navigate(location.pathname.startsWith('/admin') ? '/admin/login' : '/login', { replace: true }) }), [navigate])
  const me = useMe()
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/link" element={<LinkWarning />} />
        <Route path="/setup/:token" element={<SetupPage />} />
        <Route element={<RequireAuth />}>
          <Route path="/setup" element={<ForcedSetupPage />} />
          <Route path="/admin/*" element={me.data?.role === 'ADMIN' ? <AdminShell><AdminPage /></AdminShell> : <Navigate to="/" replace />} />
          <Route element={<AppShell />}>
            <Route path="/" element={<Navigate to="/mail/f/inbox" replace />} />
            <Route path="/mail/scheduled" element={<ScheduledPage />} />
            <Route path="/mail/f/:role/:id?" element={<MailPage />} />
            <Route path="/mail/d/:folderPath/:id?" element={<MailPage />} />
            <Route path="/mail/l/:labelId/:id?" element={<MailPage />} />
            <Route path="/mail/s/:id?" element={<MailPage />} />
 <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/settings/*" element={<SettingsPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Themed>
        <ErrorBoundary>
          <Routing />
        </ErrorBoundary>
      </Themed>
    </QueryClientProvider>
  )
}
